import $ from 'jquery';
import debounce from 'lodash/debounce';
import forIn from 'lodash/forIn';
import { Observable, Subject } from 'rxjs';
import 'gmaps-marker-clusterer'; // makes MarkerClusterer available on window object
import { DashboardSettings } from '../services/dashboard-settings';
import { isEuDomain } from '@lib/environments.const';

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

import { createMap, gmapsLoadPromise, initMapControls } from '../lib/google-maps';
import { IControl } from './control.directive';
import { ICustomPolylineOptions } from './polyline.directive';
import { WHITE } from '@lib/colors';
import { getStaticHostUrl } from '@lib/environments.const';

const MIN_CLUSTER_SIZE = 2;
const MAX_CLUSTER_ZOOM = 15;
const GRID_SIZE = 50;

declare const MarkerClusterer;

const STATIC_HOST_URL = getStaticHostUrl();
const ZOOM_CONTROL_SELECTOR = '.zoom-control';

export interface IKTMarker extends google.maps.Marker {
  ktMarkerId: number;
  animateTo: any;
  animationPromise?: Promise<void>;
  cancelAnimation?: () => Promise<void>;
}

export interface IZoomControlPositioning {
  height?: string;
  top?: string;
  margin?: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'map',
  styleUrls: ['./map.scss'],
  templateUrl: './map.component.html',
})
export class MapComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() public trafficLayerEnabled: boolean = false;
  @Input() public gmapOptions: google.maps.MapOptions = {};
  @Input() public zoomControlPosition: IZoomControlPositioning = {};
  @Input() public beforeZoomChange: Subject<number>;
  @Output() public onClick = new EventEmitter();
  @Output() public onZoomChanged = new EventEmitter();
  public shouldDisplayTraffic = false;
  public markerCluster;
  public createMapPromise;
  public markerPromiseList: Array<Promise<any>> = [];
  public map: google.maps.Map;
  public repaintCluster;
  public gmaps;
  public trafficLayer: google.maps.TrafficLayer = null;
  public el;
  private counter = 0;
  private markers = {};
  private polylines = {};
  private shapes = {};
  private updateViewport;
  private readonly trafficSettingKey: string = 'googleMapsTrafficSettingKey';
  @ViewChild('map', { static: true }) private mapContainer;
  private tilesLoadedListener: google.maps.MapsEventListener;
  private tilesLoadedSubject: Subject<void> = new Subject();

  constructor(
    private elRef: ElementRef,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    @Inject(DashboardSettings) private dashboardSettings: DashboardSettings,
  ) {
    this.el = elRef.nativeElement;
    this.updateViewport = debounce(this._updateViewport, 200);
  }

  public ngOnInit() {
    this.createMapPromise = gmapsLoadPromise
      .then(gmaps => {
        this.gmaps = gmaps;
        const baseOptions = {
          center: isEuDomain() ? { lat: 50, lng: 15 } : { lat: 39.50, lng: -98.35 },
          zoom: 4,
          minZoom: 2,
          disableDefaultUI: true,
          zoomControlOptions: {
            position: gmaps.ControlPosition.RIGHT_BOTTOM,
          },
          panControlOptions: {
            position: gmaps.ControlPosition.RIGHT_BOTTOM,
          },
          mapTypeControlOptions: {
            position: gmaps.ControlPosition.RIGHT_TOP,
          },
        };
        return createMap(this.mapContainer.nativeElement, Object.assign(baseOptions, this.gmapOptions));
      })
      .then((map: google.maps.Map) => {
        this.ngZone.runOutsideAngular(() => this.gmaps.event.addListener(map, 'click', () => this.onClick.emit()));
        this.map = map;
        this.tilesLoadedListener = this.map.addListener('tilesloaded', () => {
          this.tilesLoadedSubject.next();
        });
        initMapControls(this.el, map, this.gmapOptions, this.zoomControlPosition, this.beforeZoomChange);
        this.markerCluster = new MarkerClusterer(this.map, [], {
          ignoreHiddenMarkers: true,
          gridSize: GRID_SIZE,
          minimumClusterSize: MIN_CLUSTER_SIZE,
          maxZoom: MAX_CLUSTER_ZOOM,
          styles: [{ url: `${STATIC_HOST_URL}/map/cluster.svg`, height: 50, width: 50, textColor: WHITE, anchor: [-8, 0] }],
        });
        this.ngZone.runOutsideAngular(() => {
          this.gmaps.event.addListener(map, 'dragstart', () => {
            this.markerCluster.zoomOnClick_ = false;
          });
          this.gmaps.event.addListener(map, 'mouseup', () => {
            setTimeout(() => {
              this.markerCluster.zoomOnClick_ = true;
            }, 50);
          });
        });
        this.repaintCluster = debounce(() => this.markerCluster.repaint(), 800);

        if (this.trafficLayerEnabled) {
          this.trafficLayer = new google.maps.TrafficLayer();
          const trafficSetting = this.dashboardSettings.getKey(this.trafficSettingKey);
          const shouldDisplayTraffic = this.shouldDisplayTraffic = !!trafficSetting;
          if (shouldDisplayTraffic) this.trafficLayer.setMap(this.map);
          this.cdr.markForCheck();
        }
        return map;
      })
      ;
  }

  public ngAfterViewInit() {
    const $el = $(this.mapContainer.nativeElement);
    const resizeMapWhenVisible = () => {
      if ($el.is(':visible')) {
        this.updateViewport();
      } else setTimeout(resizeMapWhenVisible, 500);
    };
    this.createMapPromise.then(map => {
      this.ngZone.runOutsideAngular(() => {
        this.gmaps.event.addListenerOnce(map, 'idle', resizeMapWhenVisible);
        map.addListener('zoom_changed', () => this.onZoomChanged.emit(map.getZoom()));
      });
    });
  }

  public ngOnDestroy() {
    // If statement added in case we choose a group in filters which has no drivers in it
    if (this.markerCluster) this.markerCluster.clearMarkers();
    if (this.tilesLoadedListener) google.maps.event.removeListener(this.tilesLoadedListener);
  }

  public resetMap(): void {
    const baseOptions = {
      center: isEuDomain() ? { lat: 50, lng: 15 } : { lat: 39.50, lng: -98.35 },
      zoom: 4,
      minZoom: 2,
      mapTypeId: 'roadmap',
      zoomControlOptions: {
        position: this.gmaps.ControlPosition.RIGHT_BOTTOM,
      },
      panControlOptions: {
        position: this.gmaps.ControlPosition.RIGHT_BOTTOM,
      },
      mapTypeControlOptions: {
        position: this.gmaps.ControlPosition.RIGHT_TOP,
      },
    };
    this.map.setOptions(Object.assign(baseOptions, this.gmapOptions));
  }

  public toggleTraffic() {
    const shouldDisplayTraffic = this.shouldDisplayTraffic = !this.shouldDisplayTraffic;
    if (shouldDisplayTraffic) this.trafficLayer.setMap(this.map);
    else this.trafficLayer.setMap(null);
    this.dashboardSettings.setKey(this.trafficSettingKey, shouldDisplayTraffic);
  }

  public addExistingMarkerToMap(id) {
    if (this.markers[id]) this.markers[id].setMap(this.map);
  }

  public addMarkerBackToCluster(id) {
    this.markerCluster.addMarker(this.markers[id]);
  }

  public addMarkerToCluster(marker: google.maps.Marker, id: number | string = this.createId('marker')): number | string {
    this.markers[id] = marker;
    this.markerCluster.addMarker(marker);
    return id;
  }

  public addMarkersToCluster(markers: IKTMarker[]): void {
    markers.forEach((marker: IKTMarker) => {
      this.markers[marker.ktMarkerId] = marker;
    });
    this.markerCluster.addMarkers(markers);
  }

  public panTo(coords) {
    this.map.panTo(new this.gmaps.LatLng(coords.lat, coords.lng));
  }

  public panIn(coords, zoomLevel = 12, withAnimation: boolean = true): void {
    if (!this.map) return;

    let zoom = this.map.getZoom();
    const zoomMap = () => {
      this.map.setCenter(new this.gmaps.LatLng(coords.lat, coords.lng));
      if (zoom < zoomLevel) {
        zoom += 1;
        this.map.setZoom(zoom);
        setTimeout(zoomMap, 100);
      }
    };
    if (withAnimation) {
      zoomMap();
    } else {
      this.map.setCenter(new this.gmaps.LatLng(coords.lat, coords.lng));
      this.map.setZoom(zoomLevel);
    }
  }

  public addMarker(marker, options: any = {}) {
    const id = this.createId('marker');
    this.markers[id] = marker;
    marker.setMap(this.map);
    if (options.autoZoom) this.updateViewport();
    return id;
  }

  public removeMarker(id: number | string) {
    const marker: google.maps.Marker = this.markers[id];
    marker.setMap(null);
    this.markerCluster.removeMarker(marker);
    delete this.markers[id];
  }

  public deleteMarkerFromCache(id: number | string): void {
    delete this.markers[id];
  }

  public addPolyline(polyline: google.maps.Polyline, options: ICustomPolylineOptions): string {
    const id: string = this.createId('polyline');
    this.polylines[id] = polyline;
    polyline.setMap(this.map);
    if (options.autoZoom) this.updateViewport();
    return id;
  }

  public addShape(shape: any, options: any, shapeType: string): string {
    const id = this.createId(shapeType);
    this.shapes[id] = shape;
    shape.setMap(this.map);
    if (options.autoZoom) this.updateViewport();
    return id;
  }

  public removePolyline(id) {
    this.polylines[id].setMap(null);
    delete this.polylines[id];
  }

  public removeShape(id: string): void {
    this.shapes[id].setMap(null);
    delete this.shapes[id];
  }

  public addControl(control) {
    this.map.controls[this.gmaps.ControlPosition[control.position]].push(control.element);
  }

  public removeControl(control: IControl): void {
    const controls: google.maps.MVCArray<Node> = this.map.controls[this.gmaps.ControlPosition[control.position]];
    const index: number = (controls as any).indexOf(control.element);
    controls.removeAt(index);
  }

  public clearMarkers() {
    this.markerCluster.clearMarkers();
  }

  public fitMapToBounds(shouldSetZoom: boolean = true) {
    return Promise.all(this.markerPromiseList)
      .then(() => {
        const bounds = new google.maps.LatLngBounds();
        (Object as any).values(this.markers).forEach(m => {
          if (m.visible) bounds.extend(m.getPosition());
        });
        this.fitToBounds(bounds);
        if (shouldSetZoom) {
          this.map.setZoom(this.map.getZoom() - 1);
          if (this.map.getZoom() > 15) {
            this.map.setZoom(15);
          }
        }
      });
  }

  public fitToBounds(bounds: google.maps.LatLngBounds): void {
    if (!bounds.isEmpty()) {
      this.map.setCenter(bounds.getCenter());
      this.map.fitBounds(bounds);
    }
  }

  public resize() {
    this.ngZone.runOutsideAngular(() => this.gmaps.event.trigger(this.map, 'resize'));
  }

  public setZoomControl(display: boolean): void {
    this.el.querySelector(ZOOM_CONTROL_SELECTOR).style.display = display ? '' : 'none';
  }

  public getMapRadius(): number {
    const bounds = this.map.getBounds();
    const center = bounds.getCenter();
    const northEast = bounds.getNorthEast();
    // distance = circle radius from center to Northeast corner of bounds
    return google.maps.geometry.spherical.computeDistanceBetween(center, northEast);
  }

  public tilesLoaded$(): Observable<void> {
    return this.tilesLoadedSubject.asObservable();
  }

  private createId(type: string): string {
    return `${type}-${this.counter++}`;
  }

  // Returns the zoom level at which the given rectangular region fits in the map view.
  private getZoomByBounds(bounds) {
    const map = this.map;
    const MAX_ZOOM = map.mapTypes.get(map.getMapTypeId() as string).maxZoom || 21;
    const MIN_ZOOM = map.mapTypes.get(map.getMapTypeId() as string).minZoom || 0;

    const ne = map.getProjection().fromLatLngToPoint(bounds.getNorthEast());
    const sw = map.getProjection().fromLatLngToPoint(bounds.getSouthWest());

    const worldCoordWidth = Math.abs(ne.x - sw.x);
    const worldCoordHeight = Math.abs(ne.y - sw.y);

    // Fit padding in pixels
    const FIT_PAD = 40;

    for (let zoom = MAX_ZOOM; zoom >= MIN_ZOOM; --zoom) {
      if (worldCoordWidth * (1 << zoom) + 2 * FIT_PAD < $(map.getDiv()).width() &&
        worldCoordHeight * (1 << zoom) + 2 * FIT_PAD < $(map.getDiv()).height()) {
        return zoom;
      }
    }

    return 0;
  }

  private _updateViewport() {
    let coordsCount = 0;
    const bounds = new this.gmaps.LatLngBounds();
    const markers = this.markers;
    const polylines = this.polylines;
    const shapes = this.shapes;

    this.resize();

    forIn(markers, (val, key) => {
      bounds.extend(markers[key].getPosition());
      coordsCount++;
    });

    forIn(polylines, (val, key) => {
      const polyline = polylines[key];
      polyline.getPath().getArray().forEach(latLng => bounds.extend(latLng));
      coordsCount += polyline.getPath().getLength();
    });

    forIn(shapes, (val, key) => {
      const shape = shapes[key];
      shape.getPath().getArray().forEach(latLng => bounds.extend(latLng));
      coordsCount += shape.getPath().getLength();
    });

    if (coordsCount > 1) {
      forIn(markers, m => bounds.extend(m.getPosition()));
      this.map.fitBounds(bounds);
      this.gmaps.event.addListenerOnce(this.map, 'bounds_changed', event => {
        this.map.setZoom(this.getZoomByBounds(bounds));
      });
    } else if (coordsCount === 1) {
      this.map.setCenter(bounds.getCenter());
      this.map.setZoom(10);
    }
  }
}
